import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Modal,
  Button,
  Fade,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Visibility,
  Edit,
  Save,
  Close,
  Add,
} from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useParams } from 'react-router-dom';
import {
  getServiceFeeConfigurationsAPI,
  viewServiceFeeConfigurationAPI,
  updateServiceFeeConfigurationAPI,
  saveServiceFeeConfigurationAPI,
} from '../operations/supplierService';

/* ================= TYPES ================= */
interface ServiceFeeData {
  id: string;
  chargable_amount: string;
  chargable_type: string;
  created_date: string;
}

const PRODUCT_LABELS: Record<string, string> = {
  flight: 'Flight',
  hotel: 'Hotel',
  activity: 'Activity',
};

const ServiceFeeConfiguration = () => {
  const { product } = useParams<{ product: string }>();
  const productKey = product?.toLowerCase() || 'flight';
  const productLabel = PRODUCT_LABELS[productKey] || 'Flight';

  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const [editRowId, setEditRowId] = useState<string | null>(null);
  const [openedByEdit, setOpenedByEdit] = useState<boolean>(false);
  const [fees, setFees] = useState<ServiceFeeData[]>([]);
  const [editedData, setEditedData] = useState<Partial<ServiceFeeData>>({});
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [viewLoading, setViewLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [viewDataMap, setViewDataMap] = useState<Record<string, Partial<ServiceFeeData>>>({});
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMsg, setSnackbarMsg] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [newFeeErrors, setNewFeeErrors] = useState<Record<string, string>>({});
  const [newFee, setNewFee] = useState<Partial<ServiceFeeData>>({
    chargable_amount: '',
    chargable_type: '',
  });

  /* ================= SNACKBAR HELPERS ================= */
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning' = 'success'
  ) => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  /* ================= FETCH API ================= */
  const fetchFeeConfigs = async () => {
    try {
      setLoading(true);
      const response = await getServiceFeeConfigurationsAPI(productKey);
      const rawData = response?.data ?? response ?? [];

      const normalizedData: ServiceFeeData[] = (Array.isArray(rawData) ? rawData : []).map(
        (item: any) => ({
          id: item.id ?? '',
          chargable_amount: String(item.chargable_amount ?? '0'),
          chargable_type: item.chargable_type ?? '',
          created_date: item.created_date ?? '',
        })
      );

      setFees(normalizedData);
    } catch (err) {
      console.error(`Failed to fetch ${productLabel} service fee configurations:`, err);
      showSnackbar(`Failed to fetch ${productLabel} service fee configurations`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setFees([]);
    setOpenRowId(null);
    setEditRowId(null);
    setEditedData({});
    setViewDataMap({});
    fetchFeeConfigs();
  }, [productKey]);

  /* ================= VIEW API ================= */
  const toggleViewRow = async (id: string) => {
    if (openRowId === id) {
      setOpenRowId(null);
      setOpenedByEdit(false);
      return;
    }

    setOpenRowId(id);
    setEditRowId(null);
    setOpenedByEdit(false);

    if (viewDataMap[id]) return;

    try {
      setViewLoading(true);
      const response = await viewServiceFeeConfigurationAPI(productKey, { id });
      const viewResult = response?.data ?? response ?? {};

      const normalizedViewData: Partial<ServiceFeeData> = {
        chargable_amount: String(viewResult.chargable_amount ?? '0'),
        chargable_type: viewResult.chargable_type ?? '',
        created_date: viewResult.created_date ?? '',
      };

      setViewDataMap((prev) => ({ ...prev, [id]: normalizedViewData }));
      setFees((prev) =>
        prev.map((f) => (f.id === id ? { ...f, ...normalizedViewData } : f))
      );
    } catch (err) {
      console.error(`Failed to fetch ${productLabel} service fee view details:`, err);
      showSnackbar(`Failed to load ${productLabel} service fee details`, 'error');
    } finally {
      setViewLoading(false);
    }
  };

  const toggleEditRow = (id: string, fee: ServiceFeeData) => {
    if (editRowId === id) {
      setEditRowId(null);
      setEditedData({});
    } else {
      if (openRowId !== id) setOpenedByEdit(true);
      setEditRowId(id);
      setOpenRowId(id);
      setEditedData({ ...fee });
    }
  };

  /* ================= UPDATE API ================= */
  const handleSave = async (id: string) => {
    const originalFee = fees.find((f) => f.id === id);
    if (!originalFee) return;

    const hasChanges = Object.keys(editedData).some((key) => {
      const field = key as keyof ServiceFeeData;
      return editedData[field] !== originalFee[field];
    });

    if (!hasChanges) {
      showSnackbar("Didn't update anything", 'info');
      setEditRowId(null);
      setEditedData({});
      return;
    }

    try {
      setSaveLoading(true);
      const merged = { ...originalFee, ...editedData };

      const updatePayload = {
        id: merged.id,
        chargable_amount: parseFloat(merged.chargable_amount) || 0,
        chargable_type: merged.chargable_type || '',
      };

      await updateServiceFeeConfigurationAPI(productKey, updatePayload);
      showSnackbar(`${productLabel} service fee configuration updated successfully`, 'success');

      setViewDataMap((prev) => {
        const newMap = { ...prev };
        delete newMap[id];
        return newMap;
      });

      await fetchFeeConfigs();
      setEditRowId(null);
      setEditedData({});
      if (openedByEdit) {
        setOpenRowId(null);
        setOpenedByEdit(false);
      }
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message;
      console.error(`Failed to update ${productLabel} service fee configuration:`, err);
      showSnackbar(backendMsg || `Failed to update ${productLabel} service fee configuration`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCancel = () => {
    setEditRowId(null);
    setEditedData({});
    if (openedByEdit) {
      setOpenRowId(null);
      setOpenedByEdit(false);
    }
  };

  const handleEditChange = (field: keyof ServiceFeeData, value: any) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  const handleModalOpen = () => setOpenModal(true);
  const handleModalClose = () => {
    setOpenModal(false);
    setNewFeeErrors({});
    setNewFee({
      chargable_amount: '',
      chargable_type: '',
    });
  };

  const handleNewFeeChange = (field: keyof ServiceFeeData, value: any) => {
    setNewFee((prev) => ({ ...prev, [field]: value }));
    setNewFeeErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  /* ================= VALIDATION ================= */
  const validateNewFee = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    const amount = parseFloat(newFee.chargable_amount || '');
    if (isNaN(amount) || amount < 0) errors.chargable_amount = 'Enter a valid amount (>= 0)';
    if (!newFee.chargable_amount?.trim()) errors.chargable_amount = 'Please fill the fields';
    if (!newFee.chargable_type) errors.chargable_type = 'Please fill the fields';
    return errors;
  };

  /* ================= SAVE NEW API ================= */
  const handleSaveNewFee = async () => {
    const errors = validateNewFee();
    if (Object.keys(errors).length > 0) {
      setNewFeeErrors(errors);
      showSnackbar('Please fill all required fields', 'warning');
      return;
    }
    setNewFeeErrors({});
    try {
      setSaveLoading(true);

      const payload = {
        chargable_amount: parseFloat(newFee.chargable_amount || '0'),
        chargable_type: newFee.chargable_type || '',
      };

      await saveServiceFeeConfigurationAPI(productKey, payload);
      showSnackbar(`New ${productLabel} service fee configuration saved successfully`, 'success');
      handleModalClose();
      await fetchFeeConfigs();
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message;
      console.error(`Failed to save new ${productLabel} service fee configuration:`, err);
      showSnackbar(backendMsg || `Failed to save new ${productLabel} service fee configuration`, 'error');
    } finally {
      setSaveLoading(false);
    }
  };

  const capitalize = (value?: string | null) => {
    if (!value) return '-';
    return value
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatAmount = (amount: string, type: string) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '-';
    if (type === 'percentage') return `${num}%`;
    return `$${num.toFixed(2)}`;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const isEditing = (id: string) => editRowId === id;

  return (
    <>
      <Box className="headingBtnWrap" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {productLabel} Service Fee Configuration
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleModalOpen}
          sx={{
            backgroundColor: '#272727',
            '&:hover': { backgroundColor: '#1a1a1a' },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Add New
        </Button>
      </Box>

      {/* Add New Service Fee Modal */}
      <Modal
        open={openModal}
        onClose={handleModalClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
        }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: '#f5f5f5',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            className="supplierConfigurationPopup"
          >
            <CancelIcon onClick={handleModalClose} className="closeModal" />
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Add New {productLabel} Service Fee
            </Typography>

            <Box className="feeModalForm">
              <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {/* Chargable Amount */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                  <label>Chargable Amount</label>
                  <input
                    type="number"
                    className="inputField"
                    style={{ minWidth: '220px', height: '38px', ...(newFeeErrors.chargable_amount ? { border: '1px solid red' } : {}) }}
                    value={newFee.chargable_amount || ''}
                    min="0"
                    step="0.01"
                    onChange={(e) => handleNewFeeChange('chargable_amount', e.target.value)}
                  />
                  {newFeeErrors.chargable_amount && (
                    <span className="errorNewSupplier">{newFeeErrors.chargable_amount}</span>
                  )}
                </Box>

                {/* Chargable Type */}
                <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(50% - 16px)', minWidth: '200px' }}>
                  <label>Chargable Type</label>
                  <FormControl sx={{ minWidth: 220, ...(newFeeErrors.chargable_type ? { '& .commondropdown': { border: '1px solid red !important' } } : {}) }} className="commondropdownWrap">
                    <Select
                      className="commondropdown superdropdown"
                      value={newFee.chargable_type || ''}
                      onChange={(e) => handleNewFeeChange('chargable_type', e.target.value)}
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) {
                          return <span style={{ color: '#888' }}>Select Type</span>;
                        }
                        return capitalize(selected);
                      }}
                    >
                      <MenuItem value="">Select Type</MenuItem>
                      <MenuItem value="percentage">Percentage</MenuItem>
                      <MenuItem value="value">Value</MenuItem>
                    </Select>
                  </FormControl>
                  {newFeeErrors.chargable_type && (
                    <span className="errorNewSupplier">{newFeeErrors.chargable_type}</span>
                  )}
                </Box>
              </Box>

              {/* Save Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="contained"
                  onClick={handleSaveNewFee}
                  disabled={saveLoading}
                  sx={{
                    backgroundColor: '#272727',
                    '&:hover': { backgroundColor: '#272727' },
                    textTransform: 'none',
                  }}
                >
                  {saveLoading ? <CircularProgress size={24} color="inherit" /> : 'SAVE'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Table */}
      <Box
        sx={{
          border: '1px solid #e5e7eb',
          borderRadius: 2,
          overflow: 'hidden',
        }}
        className="supplierConfiguration"
      >
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: '75vh',
            overflowY: 'auto',
            backgroundColor: '#ffffff',
          }}
        >
          <Table stickyHeader>
            <TableHead
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: '#f1f3f4',
              }}
            >
              <TableRow
                sx={{
                  boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
                }}
              >
                {['CHARGABLE AMOUNT', 'CHARGABLE TYPE', 'CREATED DATE', 'ACTIONS'].map(
                  (label) => (
                    <TableCell
                      key={label}
                      align="center"
                      sx={{
                        backgroundColor: '#f1f3f4',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                      }}
                    >
                      {label}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {fees.map((fee) => {
                const isOpen = openRowId === fee.id;
                const editing = isEditing(fee.id);
                const currentData = editing ? { ...fee, ...editedData } : fee;

                return (
                  <React.Fragment key={fee.id}>
                    {/* Main Row */}
                    <TableRow hover sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        {formatAmount(fee.chargable_amount, fee.chargable_type)}
                      </TableCell>

                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            backgroundColor: fee.chargable_type === 'percentage' ? '#dbeafe' : '#f3e8ff',
                            color: fee.chargable_type === 'percentage' ? '#2563eb' : '#7c3aed',
                            border: `1px solid ${fee.chargable_type === 'percentage' ? '#93c5fd' : '#c4b5fd'}`,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'capitalize',
                          }}
                        >
                          {fee.chargable_type || '-'}
                        </Box>
                      </TableCell>

                      <TableCell align="center" sx={{ fontSize: '0.875rem', color: '#6b7280' }}>
                        {formatDate(fee.created_date)}
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {editing ? (
                            <>
                              <Tooltip title="Save">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSave(fee.id)}
                                  disabled={saveLoading}
                                  sx={{
                                    color: '#059669',
                                    '&:hover': { backgroundColor: '#d1fae5' },
                                  }}
                                >
                                  {saveLoading ? <CircularProgress size={20} /> : <Save />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  size="small"
                                  onClick={handleCancel}
                                  sx={{
                                    color: '#dc2626',
                                    '&:hover': { backgroundColor: '#fee2e2' },
                                  }}
                                >
                                  <Close />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => toggleViewRow(fee.id)}
                              >
                                <Visibility />
                              </IconButton>
                              <IconButton
                                color="secondary"
                                size="small"
                                onClick={() => toggleEditRow(fee.id, fee)}
                              >
                                <Edit />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Collapsible Details Row */}
                    <TableRow>
                      <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 4, bgcolor: '#f5f5f5' }}>
                            {viewLoading && !editing ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                                <CircularProgress />
                              </Box>
                            ) : (
                              <Box className="feeModalForm" sx={{ bgcolor: '#fff', p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Service Fee Details
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {editing && (
                                      <Button
                                        variant="contained"
                                        onClick={() => handleSave(fee.id)}
                                        disabled={saveLoading}
                                        sx={{
                                          backgroundColor: '#272727',
                                          '&:hover': { backgroundColor: '#1a1a1a' },
                                          textTransform: 'none',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {saveLoading ? <CircularProgress size={24} color="inherit" /> : 'Update'}
                                      </Button>
                                    )}
                                  </Box>
                                </Box>
                                <Box className="feeInputFieldWrap" sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                  {/* Chargable Amount */}
                                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                    <label>Chargable Amount</label>
                                    {editing ? (
                                      <input
                                        type="number"
                                        className="inputField"
                                        style={{ minWidth: '265px', height: '38px' }}
                                        value={currentData.chargable_amount}
                                        min="0"
                                        step="0.01"
                                        onChange={(e) => handleEditChange('chargable_amount', e.target.value)}
                                      />
                                    ) : (
                                      <Typography sx={{ fontSize: '14px', color: '#333' }}>
                                        {formatAmount(currentData.chargable_amount, currentData.chargable_type)}
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Chargable Type */}
                                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                    <label>Chargable Type</label>
                                    {editing ? (
                                      <FormControl sx={{ minWidth: 265 }} className="commondropdownWrap">
                                        <Select
                                          className="commondropdown superdropdown"
                                          value={currentData.chargable_type || ''}
                                          onChange={(e) => handleEditChange('chargable_type', e.target.value)}
                                          displayEmpty
                                          renderValue={(selected) => {
                                            if (!selected) {
                                              return <span style={{ color: '#888' }}>Select Type</span>;
                                            }
                                            return capitalize(selected);
                                          }}
                                        >
                                          <MenuItem value="percentage">Percentage</MenuItem>
                                          <MenuItem value="value">Value</MenuItem>
                                        </Select>
                                      </FormControl>
                                    ) : (
                                      <Typography sx={{ fontSize: '14px', color: '#333' }}>{capitalize(currentData.chargable_type)}</Typography>
                                    )}
                                  </Box>

                                  {/* Created Date (read-only) */}
                                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                    <label>Created Date</label>
                                    <Typography sx={{ fontSize: '14px', color: '#333' }}>
                                      {formatDate(currentData.created_date)}
                                    </Typography>
                                  </Box>

                                  {/* ID (read-only) */}
                                  <Box className="form-control inputFieldWrap" sx={{ flex: '1 1 calc(25% - 16px)', minWidth: '200px' }}>
                                    <label>ID</label>
                                    <Typography sx={{ fontSize: '14px', color: '#333', fontFamily: 'monospace' }}>
                                      {currentData.id}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}

              {loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}

              {!loading && fees.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8, fontWeight: 600, color: 'text.secondary' }}>
                    No service fee configurations found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Snackbar notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '50% !important',
          left: '50% !important',
          transform: 'translate(-50%, -50%) !important',
          '& .MuiSnackbarContent-root': {
            textAlign: 'center',
            minWidth: '200px',
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '1rem',
          }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ServiceFeeConfiguration;
